import React, { useState, useRef } from "react";
import "../css/HtmlEditor.css"

const ELEMENTS = [
  { type: "h1", content: "Heading 1" },
  { type: "h2", content: "Heading 2" },
  { type: "h3", content: "Heading 3" },
  { type: "h4", content: "Heading 4" },
  { type: "h5", content: "Heading 5" },
  { type: "h6", content: "Heading 6" },
  { type: "table", content: "Table" },
  { type: "p", content: "This is a paragraph." },
  { type: "img", src: "https://via.placeholder.com/100", alt: "Placeholder" },
  {
    type: "table",
    content: "<table border='1'><tr><td>Cell 1</td><td>Cell 2</td></tr></table>"
  }
];

let elementIdCounter = 0;

const HtmlEditor = () => {
  const [canvasItems, setCanvasItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const draggingItemRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  const handleDrop = (e) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData("text/plain"));
    const rect = e.target.getBoundingClientRect();

    const newItem = {
      id: elementIdCounter++,
      type: data.type,
      attributes: {
        ...data,
        content: data.content || "",
        src: data.src || "",
        alt: data.alt || ""
      },
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      width: 200,
      height: 50
    };

    setCanvasItems((prev) => [...prev, newItem]);
  };

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData("text/plain", JSON.stringify(item));
  };

  const handleMouseDown = (e, id) => {
    e.stopPropagation();
    const element = canvasItems.find((item) => item.id === id);
    draggingItemRef.current = id;
    offsetRef.current = {
      x: e.clientX - element.x,
      y: e.clientY - element.y
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (draggingItemRef.current === null) return;

    const newX = e.clientX - offsetRef.current.x;
    const newY = e.clientY - offsetRef.current.y;

    setCanvasItems((prev) =>
      prev.map((item) =>
        item.id === draggingItemRef.current
          ? { ...item, x: newX, y: newY }
          : item
      )
    );
  };

  const handleMouseUp = () => {
    draggingItemRef.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleResize = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;

    const item = canvasItems.find((el) => el.id === id);
    const startWidth = item.width;
    const startHeight = item.height;

    const onMouseMove = (e) => {
      const newWidth = startWidth + (e.clientX - startX);
      const newHeight = startHeight + (e.clientY - startY);

      setCanvasItems((prev) =>
        prev.map((el) =>
          el.id === id
            ? { ...el, width: Math.max(50, newWidth), height: Math.max(30, newHeight) }
            : el
        )
      );
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const updateAttribute = (key, value) => {
    setCanvasItems((prev) =>
      prev.map((item) =>
        item.id === selectedItem.id
          ? {
              ...item,
              attributes: {
                ...item.attributes,
                [key]: value
              }
            }
          : item
      )
    );
    setSelectedItem((prev) => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [key]: value
      }
    }));
  };

  const deleteItem = (id) => {
    setCanvasItems((prev) => prev.filter((item) => item.id !== id));
    if (selectedItem?.id === id) setSelectedItem(null);
  };

  return (
    <div className="editor-container">
      <div className="left-panel">
        <h3>HTML Elements</h3>
        {ELEMENTS.map((el, idx) => (
          <div
            key={idx}
            className="draggable-element"
            draggable
            onDragStart={(e) => handleDragStart(e, el)}
          >
            {el.type === "img" ? (
              <img src={el.src} alt={el.alt} width="80" />
            ) : el.type === "table" ? (
              <div dangerouslySetInnerHTML={{ __html: el.content }} />
            ) : (
              React.createElement(el.type, {}, el.content)
            )}
          </div>
        ))}
      </div>

      <div
        className="right-panel"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <div className="a4-paper" id="canvas">
          {canvasItems.map((item) => {
            const { id, type, attributes, x, y, width, height } = item;

            const style = {
              position: "absolute",
              top: y,
              left: x,
              width,
              height,
              border: selectedItem?.id === id ? "2px solid blue" : "1px solid #ccc",
              padding: 4,
              boxSizing: "border-box",
              cursor: "move",
              overflow: "auto",
              background: "#fff"
            };

            const resizeHandleStyle = {
              position: "absolute",
              right: 0,
              bottom: 0,
              width: 10,
              height: 10,
              background: "blue",
              cursor: "se-resize"
            };

            return (
              <div
                key={id}
                style={style}
                onMouseDown={(e) => handleMouseDown(e, id)}
                onClick={() => handleItemClick(item)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteItem(id);
                  }}
                  className="delete-btn"
                >
                  ×
                </button>

                {type === "img" ? (
                  <img
                    src={attributes.src}
                    alt={attributes.alt}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : type === "table" ? (
                  <div dangerouslySetInnerHTML={{ __html: attributes.content }} />
                ) : (
                  React.createElement(type, {}, attributes.content)
                )}

                <div
                  className="resize-handle"
                  style={resizeHandleStyle}
                  onMouseDown={(e) => handleResize(e, id)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {selectedItem && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Attributes: {selectedItem.type}</h3>
            {Object.keys(selectedItem.attributes).map((key) => (
              <div key={key} className="form-group">
                <label>{key}</label>
                <input
                  value={selectedItem.attributes[key]}
                  onChange={(e) => updateAttribute(key, e.target.value)}
                />
              </div>
            ))}
            <button onClick={() => setSelectedItem(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HtmlEditor;
